import {useCallback, useState} from 'react'
import type {AhpProvisioningTarget, AhpSpaceSelection, PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useNavigate} from '@github-ui/use-navigate'
import {useCreateEnvironmentTask, type OptimisticTaskListConfig} from './use-create-environment-task'
import {generateTaskPathForRepo, shouldUseGlobalTaskUrls, type TaskUrlScope} from '../utils/generate-task-path'
import {taskNavigationState} from '../utils/task-helpers'
import type {EnvironmentSubmit} from '@github-ui/one-copilot-composer/use-environment-selection'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import type {EarlyTaskProvisioning} from '../ahp/early-task-provisioning'
import {SANDBOX_ENVIRONMENT_ID} from '@github-ui/one-copilot-composer/environment-strip/environment-option'
import {activationTraceIdFromError, recordActivationTraceId} from '../ahp/activation-trace'
import {uuid} from '../utils/uuid'

export interface FailedEnvironmentAttempt {
  clientGeneratedId?: string
  problemStatement: string
  errorMessage: string
  timestamp: string
}

// Extracted so the try/catch below contains no inline conditional value-block,
// which the React Compiler cannot compile in that position.
function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function usesGlobalTaskUrls(taskUrlScope: TaskUrlScope): boolean {
  return shouldUseGlobalTaskUrls({
    taskUrlScope,
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })
}

interface UseEnvironmentTaskSubmitOptions {
  earlyProvisioning?: EarlyTaskProvisioning
  optimisticTaskListConfig?: OptimisticTaskListConfig
  /** Focused after a failure banner is dismissed so keyboard users don't lose
   * their place when the Dismiss button is removed (WCAG 2.4.3). */
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  /** Host-correct HTTPS clone URL forwarded to worktree provisioning; only sent
   * alongside a `repositoryNwo`, and falls back to the origin-derived URL when omitted. */
  repositoryCloneUrl?: string
  /** Controls post-create navigation. Existing callers remain repository-scoped
   * unless they explicitly opt into the global task-detail route. */
  taskUrlScope?: TaskUrlScope
  /**
   * Replaces the router navigation to the created task. Hosts outside the
   * Copilot router (e.g. the dashboard) use it to load the task page as a full
   * document, which picks up the stored launch record and sends the first turn.
   */
  navigateToTask?: (taskPath: string) => void
  chatSubmissionHandoff?: EnvironmentChatSubmissionHandoff
  /**
   * Host opt-in that hands managed-Sandbox repository submissions to the same
   * provisional task view as Chat before the task exists. Ignored unless global
   * task URLs apply, because the host routes the created task to `/copilot/tasks/:id`.
   * Other environments keep the navigate-after-creation flow.
   */
  repositorySubmissionHandoff?: EnvironmentChatSubmissionHandoff
}

export interface EnvironmentChatSubmissionHandoff {
  onSubmissionStarted: (input: {
    prompt: PreparedPrompt
    provisioningTarget: AhpProvisioningTarget
    initialSelectedSpace?: AhpSpaceSelection
    mcpPermissionMode?: McpPermissionMode
    environmentKind?: EnvironmentKind
    /** Present only for repository submissions, so a failed launch can restore the same draft. */
    repository?: {nameWithOwner: string; branch?: string}
  }) => string
  onTaskCreated: (instanceKey: string, taskId: string) => boolean
  onSubmissionFailed: (instanceKey: string, errorMessage: string) => boolean
}

/**
 * Owns AHP environment task submission for the new task input: it creates the
 * environment-bound task and navigates to it on success, and records a
 * dismissible failure banner while re-throwing errors that were not consumed by
 * an external Chat handoff.
 *
 * Every `onEnvironmentSubmit` caller must record + surface failures, because
 * `TaskComposer` propagates the rejection instead of swallowing it.
 * Sharing this hook keeps that failure handling consistent across the
 * redesigned agents page and the legacy AgentsView.
 */
export function useEnvironmentTaskSubmit({
  earlyProvisioning,
  optimisticTaskListConfig,
  textAreaRef,
  repositoryCloneUrl,
  taskUrlScope = 'repository',
  navigateToTask,
  chatSubmissionHandoff,
  repositorySubmissionHandoff,
}: UseEnvironmentTaskSubmitOptions) {
  const navigate = useNavigate()
  const {mutateAsync: createEnvironmentTask} = useCreateEnvironmentTask({
    optimisticTaskListConfig,
  })

  // Environment (AHP) task creation is a separate flow from cloud-task creation,
  // so it needs its own failed-attempt state to drive a visible banner. Without
  // this a rejected submission would only be logged and the user would see nothing.
  const [failedEnvironmentAttempts, setFailedEnvironmentAttempts] = useState<FailedEnvironmentAttempt[]>([])

  const onEnvironmentSubmit = useCallback<EnvironmentSubmit>(
    async ({
      environmentId,
      prompt,
      customAgent,
      repositoryInstructions,
      repositoryNwo,
      baseRef,
      managedSandbox,
      chatLaunch,
      agentMode,
      approvalMode,
      mcpPermissionMode,
      configureSession,
      failureOwner,
      provisioningTarget,
      scopeLogin,
      space,
      initialSelectedSpace,
      directTurnTelemetrySeed,
    }: {
      environmentId: string
      prompt: PreparedPrompt
      customAgent?: {slug: string; displayName?: string}
      repositoryInstructions?: CopilotCustomInstructions[]
      repositoryNwo?: string
      baseRef?: string
      managedSandbox?: boolean
      chatLaunch?: boolean
      agentMode?: AgentMode
      approvalMode?: Parameters<EnvironmentSubmit>[0]['approvalMode']
      mcpPermissionMode?: McpPermissionMode
      configureSession?: Parameters<EnvironmentSubmit>[0]['configureSession']
      failureOwner?: Parameters<EnvironmentSubmit>[0]['failureOwner']
      provisioningTarget?: AhpProvisioningTarget
      scopeLogin?: string
      space?: {owner: string; slug: string}
      initialSelectedSpace?: AhpSpaceSelection
      directTurnTelemetrySeed?: Parameters<EnvironmentSubmit>[0]['directTurnTelemetrySeed']
    }) => {
      if (environmentId === SANDBOX_ENVIRONMENT_ID && (chatLaunch || repositoryNwo)) {
        earlyProvisioning?.accept({scope: scopeLogin?.trim(), space, repository: repositoryNwo, branch: baseRef})
      } else {
        earlyProvisioning?.onAbandonSubmission()
      }
      const repositoryHandoff =
        repositoryNwo &&
        repositorySubmissionHandoff &&
        managedSandbox &&
        environmentId === SANDBOX_ENVIRONMENT_ID &&
        usesGlobalTaskUrls(taskUrlScope)
          ? {
              handler: repositorySubmissionHandoff,
              provisioningTarget:
                provisioningTarget?.kind === 'repository'
                  ? provisioningTarget
                  : ({kind: 'repository', nameWithOwner: repositoryNwo} as const),
              repository: {nameWithOwner: repositoryNwo, ...(baseRef ? {branch: baseRef} : {})},
            }
          : undefined
      const chatHandoff =
        !repositoryNwo && (scopeLogin || space) && provisioningTarget && chatSubmissionHandoff
          ? {handler: chatSubmissionHandoff, provisioningTarget}
          : repositoryHandoff
      let instanceKey: string | undefined
      try {
        instanceKey = chatHandoff?.handler.onSubmissionStarted({
          prompt,
          provisioningTarget: chatHandoff.provisioningTarget,
          ...(initialSelectedSpace ? {initialSelectedSpace} : {}),
          ...(managedSandbox ? {environmentKind: 'managed-sandbox' as const} : {}),
          ...(mcpPermissionMode ? {mcpPermissionMode} : {}),
          ...(repositoryHandoff ? {repository: repositoryHandoff.repository} : {}),
        })
        const {task} = await createEnvironmentTask({
          earlyProvisioning,
          environmentId,
          prompt,
          ...(customAgent ? {customAgent} : {}),
          ...(repositoryInstructions ? {repositoryInstructions} : {}),
          ...(repositoryNwo ? {repositoryNwo} : {}),
          ...(repositoryNwo && repositoryCloneUrl ? {repositoryCloneUrl} : {}),
          ...(baseRef ? {baseRef} : {}),
          ...(managedSandbox ? {managedSandbox} : {}),
          ...(chatLaunch ? {chatLaunch} : {}),
          ...(agentMode ? {agentMode} : {}),
          ...(approvalMode ? {approvalMode} : {}),
          ...(mcpPermissionMode ? {mcpPermissionMode} : {}),
          ...(configureSession ? {configureSession} : {}),
          ...(!repositoryNwo && scopeLogin ? {scopeLogin} : {}),
          ...(space ? {space} : {}),
          ...(directTurnTelemetrySeed ? {directTurnTelemetrySeed} : {}),
        })
        if (chatHandoff && instanceKey !== undefined) {
          return {status: 'submitted', ownsPromptDraft: chatHandoff.handler.onTaskCreated(instanceKey, task.id)}
        }
        const useGlobalTaskUrl = usesGlobalTaskUrls(taskUrlScope)
        const taskPath = generateTaskPathForRepo({
          taskId: task.id,
          repositoryNwo: useGlobalTaskUrl ? undefined : repositoryNwo,
        })
        if (navigateToTask) {
          navigateToTask(taskPath)
        } else {
          navigate(taskPath, {state: taskNavigationState(prompt.text)})
        }
      } catch (error) {
        earlyProvisioning?.onAbandonSubmission()
        // Build the attempt in the catch scope so the setState updater closure
        // below captures only this local — referencing the caught `error` both
        // inside that closure and in `throw error` trips a React Compiler
        // invariant (a variable used as both a local and a context reference).
        if (chatHandoff && instanceKey !== undefined) {
          recordActivationTraceId(instanceKey, activationTraceIdFromError(error))
          const failureConsumed = chatHandoff.handler.onSubmissionFailed(instanceKey, getErrorMessage(error))
          if (failureConsumed) return {status: 'failure-consumed'}
        } else if (failureOwner !== 'caller') {
          const clientGeneratedId = uuid()
          const attempt: FailedEnvironmentAttempt = {
            clientGeneratedId,
            problemStatement: prompt.text,
            errorMessage: getErrorMessage(error),
            timestamp: new Date().toISOString(),
          }
          recordActivationTraceId(clientGeneratedId, activationTraceIdFromError(error))
          setFailedEnvironmentAttempts(prev => [attempt, ...prev])
        }
        // Re-throw so ChatInput's submit handler surfaces failures not owned by
        // the external handoff and exits its "Creating..." state.
        throw error
      }
    },
    [
      chatSubmissionHandoff,
      createEnvironmentTask,
      earlyProvisioning,
      navigate,
      navigateToTask,
      repositoryCloneUrl,
      repositorySubmissionHandoff,
      taskUrlScope,
    ],
  )

  const handleEnvironmentDismiss = useCallback(
    (index: number) => {
      setFailedEnvironmentAttempts(prev => prev.filter((_, i) => i !== index))
      // Dismissing removes the focused Dismiss button, so move focus back to the
      // task input to keep keyboard users oriented (WCAG 2.4.3).
      textAreaRef?.current?.focus()
    },
    [textAreaRef],
  )

  return {onEnvironmentSubmit, failedEnvironmentAttempts, handleEnvironmentDismiss}
}
